/**
 * component-gallery.js — <media-gallery>
 *
 * The gallery is fully functional before this file runs: on mobile it is a
 * native scroll-snap rail, and on desktop CSS shows the active item. This adds
 * thumbnail control, keyboard support for the tablist, and lets the variant
 * picker jump to a variant's own image.
 */
(function () {
  'use strict';

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      if (this.items.length < 2) return;

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb) return;
        this.showMedia(thumb.dataset.mediaId, true);
      });

      this.addEventListener('keydown', this.onKeydown.bind(this));

      // Keep the thumbnails in sync when the rail is swiped rather than
      // clicked. Watching intersection is far cheaper than a scroll handler.
      if ('IntersectionObserver' in window && this.viewport) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                this.setActive(entry.target.dataset.mediaId, false);
              }
            });
          },
          { root: this.viewport, threshold: [0.6] }
        );

        this.items.forEach((item) => this.observer.observe(item));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Shows a media item.
     * @param {string|number} mediaId
     * @param {boolean} [moveFocus] True when triggered by a thumbnail click.
     */
    showMedia(mediaId, moveFocus) {
      const item = this.items.find((el) => el.dataset.mediaId === String(mediaId));
      if (!item) return;

      this.setActive(mediaId, true);

      if (this.viewport && this.viewport.scrollWidth > this.viewport.clientWidth) {
        // Horizontal rail (mobile): scroll to it rather than toggling display,
        // so the swipe position and the thumbnails agree.
        this.viewport.scrollTo({
          left: item.offsetLeft - this.viewport.offsetLeft,
          behavior: window.Theme.prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }

      if (moveFocus) {
        const image = item.querySelector('img, video, iframe');
        if (image) image.setAttribute('tabindex', '-1');
      }
    }

    /**
     * @param {string|number} mediaId
     * @param {boolean} scrollThumb Bring the matching thumbnail into view.
     */
    setActive(mediaId, scrollThumb) {
      const id = String(mediaId);

      this.items.forEach((item) => {
        item.toggleAttribute('data-active', item.dataset.mediaId === id);
      });

      this.thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.mediaId === id;
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
        // Roving tabindex: only the selected tab is in the tab order, so Tab
        // moves past the whole thumbnail strip instead of through every image.
        thumb.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive && scrollThumb && typeof thumb.scrollIntoView === 'function') {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
    }

    /** Arrow-key navigation, as expected of a tablist. */
    onKeydown(event) {
      if (!event.target.closest('[data-gallery-thumb]')) return;

      const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      event.preventDefault();

      const index = this.thumbs.indexOf(event.target.closest('[data-gallery-thumb]'));
      let next = index;

      if (event.key === 'ArrowRight') next = (index + 1) % this.thumbs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.thumbs.length) % this.thumbs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.thumbs.length - 1;

      const thumb = this.thumbs[next];
      if (!thumb) return;

      this.showMedia(thumb.dataset.mediaId, false);
      thumb.focus();
    }
  }

  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
})();
